import { useIsMdOrGreater } from '@dreamstack/feature-components/src/hooks/useWindowSize'
import { SimpleMuiButton } from '@dreamstack/feature-components/src/layout/SimpleMuiButton'
import { BodyText, HeadingH3 } from '@dreamstack/feature-components/src/layout/Texts'
import { useFormatToCurrencyCutoff, useTranslation } from '@dreamstack/i18n'
import { ChevronDown } from '@dreamstack/icons/src/icon-components/ChevronDown'
import { ChevronUp } from '@dreamstack/icons/src/icon-components/ChevronUp'
import filter from 'lodash/filter'
import flatMap from 'lodash/flatMap'
import map from 'lodash/map'
import maxBy from 'lodash/maxBy'
import uniq from 'lodash/uniq'
import dynamic from 'next/dynamic'
import type { FunctionComponent } from 'react'
import { useMemo, useState } from 'react'
import { getGraphLegendsColorId } from '../WohnKostenReport/libs/getGraphLegendsColorId'
import { getSelectedidentifiers } from '../WohnKostenReport/libs/getSelectedIdentifiers'
import { DesktopControls, MobileControls } from './BarGraph'
import { GraphControls } from './GraphControls'
import { GraphLegends } from './GraphLegends'
import { formatYears } from './Settings'
import { useGraphFontsizeCb } from './useGraphFontsize'

const ResponsiveLine = dynamic(
  () => import('@nivo/line').then((mod) => mod.ResponsiveLine),
  {
    ssr: false,
  }
)

export const LineGraph: FunctionComponent<
  React.PropsWithChildren<{
    data: { id: string; data: { x: string | number; y: number }[] }[]
    title: string
    subTitle?: string
    graphControlXLabel?: string
    graphControlYLabel?: string
    hideXControls?: boolean
    hideYControls?: boolean
    controledSelectedIdentifiers?: string[]
    controledSelectedXValues?: string[]
    colors: string[]
    enableCrossHair?: boolean
    yFormat?: boolean
    pointsEnabled?: boolean
    lineWidth?: number
    marginAxisLeft?: number
  }>
> = ({
  data,
  title,
  subTitle,
  graphControlXLabel,
  graphControlYLabel,
  hideXControls,
  hideYControls,
  controledSelectedIdentifiers,
  controledSelectedXValues,
  colors,
  enableCrossHair = false,
  lineWidth = 2,
  yFormat = true,
  pointsEnabled = true,
  marginAxisLeft = 70,
}) => {
  const formatToDecimal = useFormatToCurrencyCutoff()
  const maxY = useMemo(() => {
    const flat = flatMap(data, (t) => t.data)
    const maxVal = maxBy(flat, (f) => f.y) ?? { y: 1000 }
    return maxVal.y * 1.1
  }, [data])

  const [selectedXValues, setSelectedXValues] = useState<(string | number)[]>(
    []
  )
  const [selectedIdentifiers, setSelectedIdentifiers] = useState<string[]>([])
  const [showControls, setShowControls] = useState(false)

  const { possibleIdentifiers, possibleX } = useMemo(() => {
    const flat = map(
      flatMap(data, (d) => d.data),
      (d) => d.x
    )
    const possibleX = uniq(flat)
    setSelectedXValues(
      controledSelectedXValues ||
        map(possibleX, (p) => {
          return p
        })
    )

    const identifiers = map(data, (d) => d.id)
    const possibleIdentifiers = uniq(identifiers)
    setSelectedIdentifiers(controledSelectedIdentifiers || possibleIdentifiers)

    return { possibleX, possibleIdentifiers }
  }, [data, controledSelectedIdentifiers, controledSelectedXValues])

  const graphLegend = getGraphLegendsColorId(possibleIdentifiers, colors)
  const filteredByIdentifiers = filter(data, (d) =>
    selectedIdentifiers.includes(d.id)
  )
  const finalData = map(filteredByIdentifiers, (d) => {
    return { ...d, data: filter(d.data, (d) => selectedXValues.includes(d.x)) }
  })

  const t = useTranslation()

  const graphFontSize = useGraphFontsizeCb()
  const md = useIsMdOrGreater()
  let marginForGraph = md
    ? { top: 50, right: 120, bottom: 64, left: marginAxisLeft + 20 }
    : { top: 50, right: 40, bottom: 64, left: marginAxisLeft }

  const GraphControlsForX = (
    <GraphControls
      graphControlAllLabel={graphControlXLabel}
      possibleValues={possibleX}
      selectedValues={selectedXValues}
      setArray={setSelectedXValues}
    />
  )

  const GraphControlsForY = (
    <GraphControls
      graphControlAllLabel={graphControlYLabel}
      possibleValues={possibleIdentifiers}
      selectedValues={selectedIdentifiers}
      setArray={setSelectedIdentifiers}
    />
  )

  return (
    <div className="col-span-full lg:col-start-3 lg:col-span-8">
      <HeadingH3 className="mb-px-16">{title}</HeadingH3>
      {subTitle && <BodyText>{subTitle}</BodyText>}
      <div className="space-y-px-12 mt-px-20 md:mt-px-32">
        {/* TODO: Better solution for controls on mobile? */}
        {!(hideXControls && hideYControls) && (
          <MobileControls>
            <div className="space-y-px-16">
              <SimpleMuiButton
                onClick={() => {
                  setShowControls(!showControls)
                }}
                color={'primary'}
                variant={'text'}
                endIcon={showControls ? <ChevronUp /> : <ChevronDown />}
              >
                {t('accentro:graphControls.configureControls')}
              </SimpleMuiButton>
              {showControls && (
                <>
                  {!hideXControls && GraphControlsForX}
                  {!hideYControls && GraphControlsForY}
                </>
              )}
            </div>
          </MobileControls>
        )}
        <DesktopControls>
          {!hideXControls && GraphControlsForX}
          {!hideYControls && GraphControlsForY}
        </DesktopControls>
      </div>
      <div className="h-px-576 overflow-hidden">
        <ResponsiveLine
          theme={{
            text: {
              fontSize: graphFontSize(),
              fontFamily: 'Montserrat',
            },
          }}
          colors={colors}
          data={finalData}
          enableCrosshair={enableCrossHair}
          lineWidth={lineWidth}
          margin={marginForGraph}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: maxY,
            stacked: false,
            reverse: false,
          }}
          enableGridX={false}
          yFormat={
            yFormat ? (value) => formatToDecimal(value as number) : undefined
          }
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 8,
            tickPadding: 12,
            tickRotation: 0,
            legendOffset: 36,
            legendPosition: 'middle',
            format: (value) => formatYears(value),
          }}
          axisLeft={{
            format: (value) => formatToDecimal(value),
            tickSize: 8,
            tickPadding: 12,
            tickRotation: 0,
            legendOffset: -40,
            legendPosition: 'middle',
          }}
          pointSize={pointsEnabled ? (md ? 10 : 5) : 0}
          pointColor={{ from: 'color' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          tooltip={({ point }) => {
            return (
              <div
                style={{
                  background: 'white',
                  padding: '6px 8px',
                  border: '1px solid rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '2px',
                }}
              >
                <div
                  style={{
                    backgroundColor: point.seriesColor,
                    height: '12px',
                    width: '12px',
                    marginRight: '8px',
                  }}
                />
                <span>
                  {point.seriesId} - {point.data.xFormatted}
                  {' - '}
                  <strong>{point.data.yFormatted}</strong>
                </span>
              </div>
            )
          }}
          useMesh={true}
        />
      </div>
      <GraphLegends
        identifiers={getSelectedidentifiers(graphLegend, selectedIdentifiers)}
      />
    </div>
  )
}
